import { StackLayout } from '@euler/app/components/StackLayout';
import { SwitchIcon } from '@euler/app/components/icons/SwitchIcon';
import { Typography } from '@euler/app/components/typography';
import { useCurrentUser } from '@euler/app/flows/auth/context';
import { Avatar } from '@euler/components/Avatar';
import { Icon } from '@euler/components/Icomoon';
import { versionText } from '@euler/components/VersionText';
import { HStack } from '@euler/components/layout/Stack';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { SafeHaptics } from '@euler/utils';
import { nativeApplicationVersion } from 'expo-application';
import * as Clipboard from 'expo-clipboard';
import { FC, memo, useCallback } from 'react';
import { StyleProp, TouchableOpacity, View, ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const kAvatarSize = 80;
const kAvatarBorder = 5;

export const UserInfoView = memo(() => {
  const profile = useCurrentUser();
  const { rpx } = useResponsiveContext();

  return (
    <StackLayout direction="row" alignItems="center">
      <View
        css={`
          width: ${rpx(kAvatarSize + 2 * kAvatarBorder)}px;
          height: ${rpx(kAvatarSize + 2 * kAvatarBorder)}px;
          border-radius: ${rpx(kAvatarSize + 2 * kAvatarBorder) / 2}px;
          background-color: rgba(255, 255, 255, 0.2);
          align-items: center;
          justify-content: center;
          margin: ${rpx(32)}px ${rpx(16)}px;
        `}
      >
        <Avatar
          uri={profile?.user?.avatar}
          name={
            profile?.user.name ??
            profile?.user.nick ??
            profile?.user.userName ??
            'Unknown'
          }
          textStyle={{
            fontFamily: FontFamily.NotoSans.Bold,
            fontSize: rpx(30),
            lineHeight: rpx(44),
          }}
          css={`
            width: ${rpx(kAvatarSize)}px;
            height: ${rpx(kAvatarSize)}px;
            border-radius: ${rpx(kAvatarSize / 2)}px;
          `}
        />
      </View>
      {profile != null && (
        <StackLayout gap={rpx(4)} style={{ flex: 1 }}>
          <Typography.Text
            numberOfLines={1}
            bold
            size={rpx(24)}
            letterSpacing={rpx(1)}
            color="rgba(255, 255, 255, 0.85)"
          >
            {profile.user.name}
          </Typography.Text>
          {!profile.store.isIndependent && (
            <Typography.Text
              numberOfLines={1}
              medium
              size={rpx(10)}
              lineHeight={rpx(12)}
              color="rgba(255, 255, 255, 0.5)"
            >
              {profile?.org.name}
            </Typography.Text>
          )}
          <Typography.Text
            numberOfLines={1}
            medium
            size={rpx(10)}
            lineHeight={rpx(12)}
            color="rgba(255, 255, 255, 0.5)"
          >
            {profile?.store.name}
          </Typography.Text>
        </StackLayout>
      )}
    </StackLayout>
  );
});

export const LogoutButton: FC<{
  style?: StyleProp<ViewStyle>;
  onPress?: () => void;
}> = memo(({ style, onPress }) => {
  const { rpx } = useResponsiveContext();
  return (
    <View
      style={style}
      css={`
        align-self: flex-start;
      `}
    >
      <TouchableOpacity
        css={`
          border-width: 1px;
          border-color: rgba(255, 255, 255, 0.75);
          align-items: center;
          justify-content: center;
          height: ${rpx(28)}px;
          border-radius: ${rpx(14)}px;
          padding-left: ${rpx(12)}px;
          padding-right: ${rpx(12)}px;
        `}
        onPress={onPress}
      >
        <Typography.Text light size={rpx(12)} color="rgba(255, 255, 255, 0.85)">
          退出登录
        </Typography.Text>
      </TouchableOpacity>
    </View>
  );
});

export const SwitchAppButton: FC<{
  style?: StyleProp<ViewStyle>;
  onPress?: () => void;
}> = memo(({ style, onPress }) => {
  const { rpx } = useResponsiveContext();
  return (
    <TouchableOpacity
      style={style}
      onPress={onPress}
      css={`
        flex-direction: row;
        align-items: center;
        height: ${rpx(28)}px;
      `}
    >
      <SwitchIcon size={rpx(14)} color="rgba(255, 255, 255, 0.85)" />
      <Typography.Text
        light
        size={12}
        color="rgba(255, 255, 255, 0.85)"
        ml={rpx(4)}
      >
        切换门店
      </Typography.Text>
    </TouchableOpacity>
  );
});

export const SwitchMode = memo(
  ({
    style,
    onPress,
  }: {
    style?: StyleProp<ViewStyle>;
    onPress?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <TouchableOpacity style={style} onPress={onPress}>
        <HStack justifyContent="flex-start" alignItems="center">
          <Icon
            name="share-arrow"
            size={rpx(15)}
            color={'rgba(255, 255, 255, 0.35)'}
            css={`
              margin-right: ${rpx(2)}px;
            `}
          />
          <Typography.Label
            light
            size={rpx(12)}
            color="rgba(255, 255, 255, 0.35)"
            lineHeight={rpx(15)}
          >
            切换到演示模式
          </Typography.Label>
        </HStack>
      </TouchableOpacity>
    );
  },
);

export const VersionInfo = memo(() => {
  const { rpx } = useResponsiveContext();
  const insets = useSafeAreaInsets();
  const text = `${nativeApplicationVersion} (${versionText(8)})`;
  const onPress = useCallback(() => {
    void Clipboard.setStringAsync(text);
    SafeHaptics.impact();
  }, [text]);
  return (
    <TouchableOpacity
      onPress={onPress}
      css={`
        margin-bottom: ${insets.bottom + rpx(16)}px;
        margin-left: ${rpx(16)}px;
      `}
    >
      <Typography.Text
        light
        size={rpx(10)}
        color="rgba(255, 255, 255, 0.35)"
        mb={rpx(4)}
      >
        Powered by zhichetech.com, © 2019-2023.
      </Typography.Text>
      <Typography.Text light size={rpx(10)} color="rgba(255, 255, 255, 0.35)">
        {text}
      </Typography.Text>
    </TouchableOpacity>
  );
});
